import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  ArrowDownCircle,
  ArrowRightCircle,
  BarChart3,
  Building2,
  ChevronRight,
  DollarSign,
  Download,
  Edit,
  FileText,
  Landmark,
  MoreVertical,
  Plus,
  RefreshCw,
  Tag,
  TrendingDown,
  TrendingUp,
  Wallet,
  Banknote,
  CreditCard,
  Hash,
  Copy,
  AlertCircle,
  Save,
  Trash2,
  Power,
  PowerOff,
  ShieldAlert,
} from 'lucide-react';
import {
  ExpenseModal,
  IncomeModal,
  TransferModal,
  CurrencyConversionModal,
  CashBoxSummaryModal,
  CashBoxMovementsModal,
  CashBoxExportModal,
} from '../components/Transactions';
import cashBoxService, { CashBox } from '../services/cashBoxService';
import { showSuccess, showError, showWarning } from '../components/Alerts';
import { handleApiError } from '../utils/errorHandler';
import { AtLimit } from '../components/ui/char-counter';
import { MoneyInput } from '../components/ui/money-input';
import { useAuth } from '../context/AuthContext';
import { PERMISSIONS } from '../constants/permissions';
import RequirePermission from '../components/RequirePermission';

// ---- Config ----
const MAX_NAME = 100;
const MAX_BANK = 100;
const MAX_ACCOUNT = 50;
const MAX_CBU = 22;
const MAX_ALIAS = 50;

const ACCOUNT_TYPES = [
  { value: 'CUENTA_CORRIENTE', label: 'Cuenta Corriente' },
  { value: 'CAJA_AHORRO', label: 'Caja de Ahorro' },
  { value: 'EFECTIVO', label: 'Efectivo' },
];

const CURRENCIES = [
  { value: 'ARS', label: 'ARS (Pesos Argentinos)', symbol: '$' },
  { value: 'USD', label: 'USD (Dólares)', symbol: 'US$' },
];

const ACCOUNTING_TYPES = [
  { value: 'A', label: 'Contabilidad A' },
  { value: 'B', label: 'Contabilidad B' },
  { value: 'AMBAS', label: 'Ambas Contabilidades' },
];

interface CashBoxForm {
  name: string;
  bank: string;
  accountType: 'CUENTA_CORRIENTE' | 'CAJA_AHORRO' | 'EFECTIVO';
  accountNumber: string;
  currency: 'ARS' | 'USD';
  cbu: string;
  alias: string;
  balance: number;
  isActive: boolean;
  accountingType: 'A' | 'B' | 'AMBAS';
}

const EMPTY_FORM: CashBoxForm = {
  name: '',
  bank: '',
  accountType: 'CUENTA_CORRIENTE',
  accountNumber: '',
  currency: 'ARS',
  cbu: '',
  alias: '',
  balance: 0,
  isActive: true,
  accountingType: 'A',
};

// ---- Helpers ----
const mapAccountType = (type: string): CashBoxForm['accountType'] => {
  const map: Record<string, CashBoxForm['accountType']> = {
    'Cuenta Corriente': 'CUENTA_CORRIENTE',
    'Caja de Ahorro': 'CAJA_AHORRO',
    Efectivo: 'EFECTIVO',
    CUENTA_CORRIENTE: 'CUENTA_CORRIENTE',
    CAJA_AHORRO: 'CAJA_AHORRO',
    EFECTIVO: 'EFECTIVO',
  };
  return map[type] || 'CUENTA_CORRIENTE';
};

const mapAccountingType = (type: string): CashBoxForm['accountingType'] => {
  const map: Record<string, CashBoxForm['accountingType']> = {
    'Contabilidad General': 'A', 'Contabilidad A': 'A', 'Contabilidad B': 'B',
    'Ambas Contabilidades': 'AMBAS', A: 'A', B: 'B', AMBAS: 'AMBAS',
  };
  return map[type] || 'A';
};

const mapAccountTypeToBackend = (type: string): string => ({
  CUENTA_CORRIENTE: 'Cuenta Corriente',
  CAJA_AHORRO: 'Caja de Ahorro',
  EFECTIVO: 'Efectivo',
}[type] || type);

const getAccountTypeLabel = (type: string) =>
  ACCOUNT_TYPES.find((t) => t.value === type)?.label || type;

const getAccountingTypeLabel = (type: string) =>
  ACCOUNTING_TYPES.find((t) => t.value === type)?.label || type;

const getCurrencySymbol = (currency: string) =>
  CURRENCIES.find((c) => c.value === currency)?.symbol || '$';

const formatCurrency = (amount: number | null | undefined, currency: string) => {
  const symbol = getCurrencySymbol(currency);
  return `${symbol} ${(amount ?? 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const getAccountIcon = (type: string, cls = 'w-4 h-4') => {
  const mapped = mapAccountType(type);
  if (mapped === 'EFECTIVO') return <Banknote className={cls} />;
  if (mapped === 'CAJA_AHORRO') return <Wallet className={cls} />;
  return <Landmark className={cls} />;
};

const getAccountColor = (box: CashBox) => {
  const mapped = mapAccountType(box.accountType);
  if (!box.isActive) return { bg: 'bg-muted', text: 'text-muted-foreground' };
  if (mapped === 'EFECTIVO') return { bg: 'bg-success/10', text: 'text-success' };
  if (box.currency === 'USD') return { bg: 'bg-info/10', text: 'text-info' };
  return { bg: 'bg-primary/10', text: 'text-primary' };
};

// ---- CashBoxCard ----
interface CashBoxCardProps {
  box: CashBox;
  onEdit: (box: CashBox) => void;
  onDelete: (box: CashBox) => void;
  onReactivate: (id: number) => void;
  onIncome: (id: number) => void;
  onExpense: (id: number) => void;
  onSummary: (id: number) => void;
  onMovements: (id: number) => void;
  onExport: (id: number) => void;
  canOperate: boolean;
  canReport: boolean;
}

function CashBoxCard({ box, onEdit, onDelete, onReactivate, onIncome, onExpense, onSummary, onMovements, onExport, canOperate, canReport }: CashBoxCardProps) {
  const accountType = mapAccountType(box.accountType);
  const isActive = box.isActive ?? true;
  const colors = getAccountColor(box);
  const balance = box.balance ?? 0;
  const isNegative = balance < 0;

  return (
    <div className={`flex flex-col rounded-xl border bg-card transition-all duration-200 overflow-hidden ${
      isActive ? 'border-border hover:shadow-md' : 'opacity-70 border-border/60 bg-muted/20'
    }`}>

      {/* Header */}
      <div className="flex items-start justify-between gap-3 px-4 pt-4 pb-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${colors.bg} ${colors.text}`}>
            {getAccountIcon(box.accountType)}
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold leading-snug truncate text-foreground">{box.name}</p>
            <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
              <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded-md border border-border text-muted-foreground">
                {getAccountTypeLabel(accountType)}
              </span>
              {isActive ? (
                <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-success/10 text-success">
                  Activa
                </span>
              ) : (
                <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-muted text-muted-foreground">
                  Inactiva
                </span>
              )}
              {accountType === 'EFECTIVO' && box.accountingType && (
                <span className="inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded-md border border-border text-muted-foreground">
                  {getAccountingTypeLabel(mapAccountingType(box.accountingType))}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* ⋮ Dropdown menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="p-1.5 rounded-lg border border-border bg-background text-muted-foreground hover:bg-muted hover:text-foreground transition-colors shrink-0">
              <MoreVertical className="w-4 h-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            {isActive && canOperate && (
              <>
                <DropdownMenuItem
                  onClick={() => onIncome(box.id)}
                  className="gap-2 cursor-pointer text-success focus:text-success focus:bg-success/10"
                >
                  <ArrowDownCircle className="w-4 h-4" />
                  Registrar Ingreso
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => onExpense(box.id)}
                  className="gap-2 cursor-pointer text-destructive focus:text-destructive focus:bg-destructive/10"
                >
                  <TrendingDown className="w-4 h-4" />
                  Registrar Egreso
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            {canReport && (
              <>
                <DropdownMenuItem onClick={() => onSummary(box.id)} className="gap-2 cursor-pointer">
                  <BarChart3 className="w-4 h-4 text-primary" />
                  Ver Resumen Diario
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onMovements(box.id)} className="gap-2 cursor-pointer">
                  <FileText className="w-4 h-4 text-muted-foreground" />
                  Ver Movimientos
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onExport(box.id)} className="gap-2 cursor-pointer">
                  <Download className="w-4 h-4 text-success" />
                  Exportar a Excel
                </DropdownMenuItem>
              </>
            )}
            {canOperate && (
              <>
                <DropdownMenuSeparator />
                {!isActive && (
                  <DropdownMenuItem
                    onClick={() => onReactivate(box.id)}
                    className="gap-2 cursor-pointer text-success focus:text-success focus:bg-success/10"
                  >
                    <Power className="w-4 h-4" />
                    Reactivar Caja
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => onEdit(box)} className="gap-2 cursor-pointer">
                  <Edit className="w-4 h-4" />
                  Editar Caja
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => onDelete(box)}
                  className="gap-2 cursor-pointer text-destructive focus:text-destructive focus:bg-destructive/10"
                >
                  {box.isActive ? <PowerOff className="w-4 h-4" /> : <Trash2 className="w-4 h-4" />}
                  {box.isActive ? 'Desactivar Caja' : 'Eliminar Caja'}
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Balance */}
      <div className={`mx-4 mb-3 px-3 py-3 rounded-lg ${
        isNegative ? 'bg-destructive/5 border border-destructive/20' : 'bg-muted/40 border border-border'
      }`}>
        <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-0.5">Saldo Actual</p>
        <p className={`text-2xl font-bold tabular-nums tracking-tight leading-none ${isNegative ? 'text-destructive' : 'text-foreground'}`}>
          {formatCurrency(balance, box.currency)}
        </p>
        <p className="text-[10px] text-muted-foreground mt-1">
          {box.currency === 'USD' ? 'Dólares estadounidenses' : 'Pesos argentinos'}
          {isNegative && <span className="ml-1.5 text-destructive font-medium">· Saldo negativo</span>}
        </p>
      </div>

      {/* Bank data */}
      {accountType !== 'EFECTIVO' && (box.bank || box.accountNumber || box.alias || box.cbu) && (
        <div className="mx-4 mb-4 overflow-hidden border rounded-lg border-border">
          <div className="px-3 py-1.5 bg-muted/40 border-b border-border">
            <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">Datos Bancarios</p>
          </div>
          <div className="divide-y divide-border">
            {box.bank && (
              <div className="flex items-center justify-between gap-3 px-3 py-2">
                <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground shrink-0">
                  <Landmark className="w-3 h-3" />Banco
                </span>
                <span className="text-xs font-medium text-right truncate text-foreground">{box.bank}</span>
              </div>
            )}
            {box.accountNumber && (
              <div className="flex items-center justify-between gap-3 px-3 py-2">
                <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground shrink-0">
                  <Hash className="w-3 h-3" />N° de Cuenta
                </span>
                <span className="font-mono text-xs text-foreground">{box.accountNumber}</span>
              </div>
            )}
            {box.alias && (
              <div className="flex items-center justify-between gap-3 px-3 py-2">
                <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground shrink-0">
                  <Tag className="w-3 h-3" />Alias
                </span>
                {isActive ? (
                  <button
                    onClick={() => { navigator.clipboard.writeText(box.alias!); showSuccess('Alias copiado al portapapeles'); }}
                    className="flex items-center gap-1 font-mono text-xs group/copy text-foreground hover:text-primary transition-colors"
                  >
                    {box.alias}
                    <Copy className="w-3 h-3 opacity-0 group-hover/copy:opacity-100 transition-opacity" />
                  </button>
                ) : (
                  <span className="font-mono text-xs text-foreground">{box.alias}</span>
                )}
              </div>
            )}
            {box.cbu && (
              <div className="flex items-center justify-between gap-3 px-3 py-2">
                <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground shrink-0">
                  <CreditCard className="w-3 h-3" />CBU
                </span>
                {isActive ? (
                  <button
                    onClick={() => { navigator.clipboard.writeText(box.cbu!); showSuccess('CBU copiado al portapapeles'); }}
                    className="group/copy flex items-center gap-1 text-[11px] font-mono text-muted-foreground hover:text-primary transition-colors"
                  >
                    {box.cbu.slice(0, 6)}·····{box.cbu.slice(-4)}
                    <Copy className="w-3 h-3 opacity-0 group-hover/copy:opacity-100 transition-opacity" />
                  </button>
                ) : (
                  <span className="text-[11px] font-mono text-muted-foreground">
                    {box.cbu.slice(0, 6)}·····{box.cbu.slice(-4)}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Page ----
export default function Cashflow() {
  const { hasPermission } = useAuth();
  const canOperateCashBoxes = hasPermission(PERMISSIONS.CONTABILIDAD.CAJAS_OPERAR);
  const canViewReports = hasPermission(PERMISSIONS.CONTABILIDAD.REPORTES_VER);

  const [expenseModalOpen, setExpenseModalOpen] = useState(false);
  const [incomeModalOpen, setIncomeModalOpen] = useState(false);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [conversionModalOpen, setConversionModalOpen] = useState(false);
  const [preselectedCashBoxId, setPreselectedCashBoxId] = useState<number | undefined>(undefined);
  const [showInactiveCashBoxes, setShowInactiveCashBoxes] = useState(false);

  const [summaryModalOpen, setSummaryModalOpen] = useState(false);
  const [movementsModalOpen, setMovementsModalOpen] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [selectedReportCashBoxId, setSelectedReportCashBoxId] = useState<number | null>(null);

  const [cashBoxModalOpen, setCashBoxModalOpen] = useState(false);
  const [cashBoxes, setCashBoxes] = useState<CashBox[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [editingId, setEditingId] = useState<number | null>(null);
  const [formData, setFormData] = useState<CashBoxForm>({ ...EMPTY_FORM });
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [nameInvalid, setNameInvalid] = useState(false);
  const [bankInvalid, setBankInvalid] = useState(false);
  const [accountNumberInvalid, setAccountNumberInvalid] = useState(false);
  const [saving, setSaving] = useState(false);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteTargetBox, setDeleteTargetBox] = useState<CashBox | null>(null);
  const [deleteAction, setDeleteAction] = useState(false);

  const loadCashBoxes = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      setCashBoxes(await cashBoxService.getAllCashBoxes());
    } catch {
      setError('No se pudieron cargar las cajas. Intente nuevamente.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCashBoxes(); }, [loadCashBoxes]);

  const handleExpenseSuccess = () => { showSuccess('Egreso registrado correctamente'); loadCashBoxes(); };
  const handleIncomeSuccess = () => { showSuccess('Ingreso registrado correctamente'); loadCashBoxes(); };
  const handleTransferSuccess = () => { showSuccess('Transferencia registrada correctamente'); loadCashBoxes(); };
  const handleConversionSuccess = () => { showSuccess('Conversión de moneda registrada correctamente'); loadCashBoxes(); };

  const activeCashBoxes = cashBoxes.filter((b) => b.isActive !== false);
  const inactiveCashBoxes = cashBoxes.filter((b) => b.isActive === false);

  const totals = cashBoxes.reduce(
    (acc, box) => {
      if (box.isActive) {
        if (box.currency === 'ARS') acc.ars += box.balance ?? 0;
        else if (box.currency === 'USD') acc.usd += box.balance ?? 0;
      }
      return acc;
    },
    { ars: 0, usd: 0 }
  );

  // ---- CRUD ----
  const validateForm = (): boolean => {
    let valid = true;
    if (!formData.name.trim()) { setNameInvalid(true); valid = false; }
    if (formData.accountType !== 'EFECTIVO') {
      if (!formData.bank.trim()) { setBankInvalid(true); valid = false; }
      if (!formData.accountNumber.trim()) { setAccountNumberInvalid(true); valid = false; }
    }
    if (formData.cbu && !/^\d{22}$/.test(formData.cbu)) {
      setValidationErrors((prev) => ({ ...prev, cbu: 'El CBU debe tener exactamente 22 dígitos numéricos' }));
      valid = false;
    }
    if (!valid) showWarning('Por favor, completá los campos obligatorios');
    return valid;
  };

  const openCreateModal = () => {
    if (!canOperateCashBoxes) { showError('No tiene permisos para operar cajas.'); return; }
    setEditingId(null);
    setFormData({ ...EMPTY_FORM });
    setValidationErrors({});
    setNameInvalid(false); setBankInvalid(false); setAccountNumberInvalid(false);
    setCashBoxModalOpen(true);
  };

  const openEditModal = (box: CashBox) => {
    if (!canOperateCashBoxes) { showError('No tiene permisos para operar cajas.'); return; }
    setEditingId(box.id);
    setFormData({
      name: box.name,
      bank: box.bank || '',
      accountType: mapAccountType(box.accountType),
      accountNumber: box.accountNumber || '',
      currency: (box.currency as 'ARS' | 'USD') || 'ARS',
      cbu: box.cbu || '',
      alias: box.alias || '',
      balance: box.balance ?? 0,
      isActive: box.isActive ?? true,
      accountingType: mapAccountingType(box.accountingType),
    });
    setValidationErrors({});
    setNameInvalid(false); setBankInvalid(false); setAccountNumberInvalid(false);
    setCashBoxModalOpen(true);
  };

  const closeCashBoxModal = () => {
    setCashBoxModalOpen(false);
    setEditingId(null);
    setFormData({ ...EMPTY_FORM });
    setValidationErrors({});
    setNameInvalid(false); setBankInvalid(false); setAccountNumberInvalid(false);
  };

  const handleSaveCashBox = async () => {
    if (!canOperateCashBoxes) { showError('No tiene permisos para operar cajas.'); return; }
    if (!validateForm()) return;
    try {
      setSaving(true);
      if (editingId) {
        const updated = await cashBoxService.updateCashBox(editingId, {
          name: formData.name,
          bank: formData.bank || undefined,
          accountNumber: formData.accountNumber || undefined,
          cbu: formData.cbu || undefined,
          alias: formData.alias || undefined,
          isActive: formData.isActive,
          accountingType: formData.accountingType,
        });
        setCashBoxes((prev) => prev.map((b) => (b.id === editingId ? updated : b)));
        showSuccess('Caja actualizada correctamente');
      } else {
        const payload: any = {
          name: formData.name,
          accountType: mapAccountTypeToBackend(formData.accountType),
          currency: formData.currency,
          balance: formData.balance,
          isActive: formData.isActive,
          accountingType: formData.accountingType,
        };
        if (formData.accountType !== 'EFECTIVO') {
          if (formData.bank) payload.bank = formData.bank;
          if (formData.accountNumber) payload.accountNumber = formData.accountNumber;
          if (formData.cbu) payload.cbu = formData.cbu;
          if (formData.alias) payload.alias = formData.alias;
        }
        const created = await cashBoxService.createCashBox(payload);
        setCashBoxes((prev) => [...prev, created]);
        showSuccess('Caja creada correctamente');
      }
      closeCashBoxModal();
    } catch (e: any) {
      handleApiError(e, 'Error al guardar la caja');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteModal = (box: CashBox) => {
    if (!canOperateCashBoxes) { showError('No tiene permisos para operar cajas.'); return; }
    setDeleteTargetBox(box);
    setDeleteAction(false);
    setDeleteModalOpen(true);
  };

  const handleDeactivateCashBox = async () => {
    if (!deleteTargetBox) return;
    try {
      setDeleteAction(true);
      const updated = await cashBoxService.updateCashBox(deleteTargetBox.id, { isActive: false });
      setCashBoxes((prev) => prev.map((b) => (b.id === deleteTargetBox.id ? updated : b)));
      showSuccess('Caja desactivada correctamente');
      setDeleteModalOpen(false);
    } catch (e: any) {
      handleApiError(e, 'Error al desactivar la caja');
    } finally {
      setDeleteAction(false);
    }
  };

  const handleReactivateCashBox = async (id: number) => {
    if (!canOperateCashBoxes) { showError('No tiene permisos para operar cajas.'); return; }
    try {
      const updated = await cashBoxService.updateCashBox(id, { isActive: true });
      setCashBoxes((prev) => prev.map((b) => (b.id === id ? updated : b)));
      showSuccess('Caja reactivada correctamente');
    } catch (e: any) {
      handleApiError(e, 'Error al reactivar la caja');
    }
  };

  const handleDeleteCashBox = async () => {
    if (!deleteTargetBox) return;
    try {
      setDeleteAction(true);
      await cashBoxService.deleteCashBox(deleteTargetBox.id);
      setCashBoxes((prev) => prev.filter((b) => b.id !== deleteTargetBox.id));
      showSuccess('Caja eliminada correctamente');
      setDeleteModalOpen(false);
    } catch (e: any) {
      handleApiError(e, 'Error al eliminar la caja');
    } finally {
      setDeleteAction(false);
    }
  };

  const updateField = (field: keyof CashBoxForm, value: any) => {
    if (field === 'name' && value?.trim()) setNameInvalid(false);
    if (field === 'bank' && value?.trim()) setBankInvalid(false);
    if (field === 'accountNumber' && value?.trim()) setAccountNumberInvalid(false);
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // ---- Render ----
  return (
    <div className="w-full h-full p-4 sm:p-6">
      <div className="overflow-hidden border rounded-xl shadow-sm bg-card border-border">

      {/* ── Header ── */}
      <div className="px-4 py-5 border-b sm:px-6 border-border">
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-success/10 flex items-center justify-center shrink-0">
              <Wallet className="w-5 h-5 text-success" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Flujo de Caja</h1>
              <p className="mt-1 text-sm text-muted-foreground">Cajas, movimientos, transferencias y reportes</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={loadCashBoxes} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualizar
            </Button>
            <RequirePermission permission={PERMISSIONS.CONTABILIDAD.CAJAS_OPERAR}>
              <Button variant="outline" size="sm" onClick={() => setConversionModalOpen(true)}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Cambio de Moneda
              </Button>
            </RequirePermission>
            <RequirePermission permission={PERMISSIONS.CONTABILIDAD.CAJAS_OPERAR}>
              <Button variant="outline" size="sm" onClick={() => setTransferModalOpen(true)}>
                <ArrowRightCircle className="w-4 h-4 mr-2" />
                Transferencia
              </Button>
            </RequirePermission>
            <RequirePermission permission={PERMISSIONS.CONTABILIDAD.CAJAS_OPERAR}>
              <Button size="sm" onClick={openCreateModal} className="bg-primary text-primary-foreground hover:opacity-90 rounded-lg">
                <Plus className="w-4 h-4 mr-2" />
                Nueva Caja
              </Button>
            </RequirePermission>
          </div>
        </div>
      </div>

      {/* ── Cuerpo ── */}
      <div className="p-4 space-y-6 sm:p-6">

        {/* Error */}
        {error && (
          <div className="flex items-center gap-3 px-4 py-3 text-sm border text-destructive border-destructive/20 rounded-xl bg-destructive/5">
            <AlertCircle className="w-4 h-4 shrink-0" />
            {error}
          </div>
        )}

        {/* Stats */}
        <div className="bg-background border border-border rounded-xl overflow-hidden">

          {/* Fila principal: saldos + contadores */}
          <div className="flex flex-col gap-4 px-4 pt-4 pb-3 sm:flex-row sm:items-center sm:justify-between">

            {/* Saldos por moneda */}
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mr-0.5 shrink-0">
                Saldos:
              </span>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-success/25 bg-success/8 text-success">
                <DollarSign className="w-3 h-3 shrink-0" />
                <span className="text-xs font-bold tabular-nums">{formatCurrency(totals.ars, 'ARS')}</span>
                <span className="text-[10px] font-medium opacity-70">ARS</span>
              </div>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-info/25 bg-info/8 text-info">
                <DollarSign className="w-3 h-3 shrink-0" />
                <span className="text-xs font-bold tabular-nums">{formatCurrency(totals.usd, 'USD')}</span>
                <span className="text-[10px] font-medium opacity-70">USD</span>
              </div>
            </div>

            {/* Contadores de cajas */}
            <div className="flex items-center gap-2 flex-wrap shrink-0">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground shrink-0">
                Cajas:
              </span>
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-border bg-primary/8 text-primary">
                <Building2 className="w-3 h-3 shrink-0" />
                <span className="text-xs font-bold tabular-nums">{activeCashBoxes.length}</span>
                <span className="text-[10px] font-medium opacity-70">activa{activeCashBoxes.length !== 1 ? 's' : ''}</span>
              </div>
              {inactiveCashBoxes.length > 0 && (
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-border bg-muted/60 text-muted-foreground">
                  <Wallet className="w-3 h-3 shrink-0" />
                  <span className="text-xs font-bold tabular-nums">{inactiveCashBoxes.length}</span>
                  <span className="text-[10px] font-medium">inactiva{inactiveCashBoxes.length !== 1 ? 's' : ''}</span>
                </div>
              )}
            </div>
          </div>

          {/* Barra de actividad de cajas */}
          {cashBoxes.length > 0 && (
            <div className="px-4 pb-4">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] font-medium text-muted-foreground">
                  {activeCashBoxes.length} de {cashBoxes.length} caja{cashBoxes.length !== 1 ? 's' : ''} activa{activeCashBoxes.length !== 1 ? 's' : ''}
                </span>
                <span className="text-[10px] font-bold text-muted-foreground tabular-nums">
                  {cashBoxes.length > 0 ? Math.round((activeCashBoxes.length / cashBoxes.length) * 100) : 0}%
                </span>
              </div>
              <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${cashBoxes.length > 0 ? (activeCashBoxes.length / cashBoxes.length) * 100 : 0}%`,
                    background: 'linear-gradient(90deg, hsl(var(--primary)) 0%, hsl(var(--primary)/0.7) 100%)',
                  }}
                />
              </div>
            </div>
          )}
        </div>

        {/* Cajas y Cuentas */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="w-6 h-6 mr-3 animate-spin text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Cargando cajas...</span>
          </div>
        ) : cashBoxes.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-center border border-border rounded-xl">
            <div className="flex items-center justify-center w-16 h-16 mb-4 rounded-2xl bg-muted/50">
              <Building2 className="w-8 h-8 text-muted-foreground/40" />
            </div>
            <p className="text-base font-semibold text-foreground">Sin cajas configuradas</p>
            <p className="mt-1 mb-5 text-sm text-muted-foreground">Creá tu primera caja para comenzar a operar</p>
            <RequirePermission permission={PERMISSIONS.CONTABILIDAD.CAJAS_OPERAR}>
              <Button onClick={openCreateModal} variant="outline" size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Crear primera caja
              </Button>
            </RequirePermission>
          </div>
        ) : (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-foreground">
                Cajas y Cuentas Bancarias
              </h2>
              <span className="text-xs text-muted-foreground">
                {activeCashBoxes.length} activa{activeCashBoxes.length !== 1 ? 's' : ''}
              </span>
            </div>

            {/* Active */}
            {activeCashBoxes.length > 0 && (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {activeCashBoxes.map((box) => (
                  <CashBoxCard
                    key={box.id}
                    box={box}
                    canOperate={canOperateCashBoxes}
                    canReport={canViewReports}
                    onEdit={openEditModal}
                    onDelete={openDeleteModal}
                    onReactivate={handleReactivateCashBox}
                    onIncome={(id) => { setPreselectedCashBoxId(id); setIncomeModalOpen(true); }}
                    onExpense={(id) => { setPreselectedCashBoxId(id); setExpenseModalOpen(true); }}
                    onSummary={(id) => { setSelectedReportCashBoxId(id); setSummaryModalOpen(true); }}
                    onMovements={(id) => { setSelectedReportCashBoxId(id); setMovementsModalOpen(true); }}
                    onExport={(id) => { setSelectedReportCashBoxId(id); setExportModalOpen(true); }}
                  />
                ))}
              </div>
            )}

            {/* Inactive — collapsible */}
            {inactiveCashBoxes.length > 0 && (
              <div>
                <button
                  onClick={() => setShowInactiveCashBoxes((prev) => !prev)}
                  className="flex items-center gap-2 mb-3 text-sm transition-colors text-muted-foreground hover:text-foreground"
                >
                  <ChevronRight className={`w-4 h-4 transition-transform ${showInactiveCashBoxes ? 'rotate-90' : ''}`} />
                  Cajas inactivas ({inactiveCashBoxes.length})
                </button>
                {showInactiveCashBoxes && (
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {inactiveCashBoxes.map((box) => (
                      <CashBoxCard
                        key={box.id}
                        box={box}
                        canOperate={canOperateCashBoxes}
                        canReport={canViewReports}
                        onEdit={openEditModal}
                        onDelete={openDeleteModal}
                        onReactivate={handleReactivateCashBox}
                        onIncome={(id) => { setPreselectedCashBoxId(id); setIncomeModalOpen(true); }}
                        onExpense={(id) => { setPreselectedCashBoxId(id); setExpenseModalOpen(true); }}
                        onSummary={(id) => { setSelectedReportCashBoxId(id); setSummaryModalOpen(true); }}
                        onMovements={(id) => { setSelectedReportCashBoxId(id); setMovementsModalOpen(true); }}
                        onExport={(id) => { setSelectedReportCashBoxId(id); setExportModalOpen(true); }}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ==================== MODAL CREAR/EDITAR CAJA ==================== */}
      <Dialog open={cashBoxModalOpen} onOpenChange={(open) => { if (!open) closeCashBoxModal(); }}>
        <DialogContent className="w-[95vw] max-w-4xl bg-card border-border p-0 max-h-[90vh] flex flex-col">

          <DialogHeader className="shrink-0 px-6 pt-6 pb-4 border-b border-border">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                <Building2 className="w-4 h-4 text-primary" />
              </div>
              <div>
                <DialogTitle className="text-base font-bold text-foreground">
                  {editingId ? 'Editar Caja' : 'Nueva Caja'}
                </DialogTitle>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {editingId ? 'Modificá la información de la cuenta' : 'Agregá una caja o cuenta bancaria'}
                </p>
              </div>
            </div>
          </DialogHeader>

          {/* Secciones del formulario */}
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-8">

            {/* Sección: Información básica */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <div className="lg:col-span-1">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
                  <Building2 className="w-4 h-4 text-primary" />
                  Información Básica
                </h3>
                <p className="mt-1 text-xs text-muted-foreground">Nombre e identificación de la caja.</p>
              </div>
              <div className="space-y-4 lg:col-span-2">
                <div className="space-y-2">
                  <Label htmlFor="cb-name" className="text-xs font-medium text-foreground sm:text-sm">
                    Nombre <span className="text-xs text-red-500">(Campo obligatorio)</span>
                  </Label>
                  <Input
                    id="cb-name"
                    value={formData.name}
                    onChange={(e) => { if (e.target.value.length <= MAX_NAME) updateField('name', e.target.value); }}
                    maxLength={MAX_NAME}
                    placeholder="Ej: Cuenta principal, Caja chica..."
                    className={`rounded-lg bg-background border-border focus-visible:ring-primary ${nameInvalid ? 'border-red-500 border-2 focus-visible:ring-red-500' : ''}`}
                  />
                  <AtLimit value={formData.name} max={MAX_NAME} />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-foreground sm:text-sm">
                      Tipo de Cuenta <span className="text-xs text-red-500">*</span>
                    </Label>
                    {editingId ? (
                      <div className="flex items-center h-10 px-3 text-sm border rounded-lg border-border bg-muted/40 text-muted-foreground">
                        {getAccountTypeLabel(formData.accountType)}
                      </div>
                    ) : (
                      <Select value={formData.accountType} onValueChange={(v: any) => {
                        if (v === 'EFECTIVO') setFormData((p) => ({ ...p, accountType: v, bank: '', accountNumber: '', cbu: '', alias: '' }));
                        else setFormData((p) => ({ ...p, accountType: v, accountingType: 'A' }));
                        setValidationErrors({});
                      }}>
                        <SelectTrigger className="rounded-lg bg-background border-border">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {ACCOUNT_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-foreground sm:text-sm">
                      Moneda <span className="text-xs text-red-500">*</span>
                    </Label>
                    {editingId ? (
                      <div className="flex items-center h-10 px-3 text-sm border rounded-lg border-border bg-muted/40 text-muted-foreground">
                        {CURRENCIES.find((c) => c.value === formData.currency)?.label || formData.currency}
                      </div>
                    ) : (
                      <Select value={formData.currency} onValueChange={(v: any) => updateField('currency', v)}>
                        <SelectTrigger className="rounded-lg bg-background border-border">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CURRENCIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </div>
                {editingId && (
                  <p className="text-xs text-muted-foreground">El tipo de cuenta y la moneda no pueden modificarse.</p>
                )}

                {formData.accountType === 'EFECTIVO' && (
                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-foreground sm:text-sm">Tipo de Contabilidad <span className="text-xs text-red-500">*</span></Label>
                    <Select value={formData.accountingType} onValueChange={(v: any) => updateField('accountingType', v)}>
                      <SelectTrigger className="rounded-lg bg-background border-border">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ACCOUNTING_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Tipo A: gravadas con IVA · Tipo B: no gravadas · Ambas: cualquier comprobante.
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Sección: Datos bancarios (solo no-efectivo) */}
            {formData.accountType !== 'EFECTIVO' && (
              <>
                <div className="border-t border-border" />
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                  <div className="lg:col-span-1">
                    <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
                      <Landmark className="w-4 h-4 text-primary" />
                      Datos Bancarios
                    </h3>
                    <p className="mt-1 text-xs text-muted-foreground">Información de la cuenta bancaria.</p>
                  </div>
                  <div className="space-y-4 lg:col-span-2">
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-foreground sm:text-sm">
                        Banco <span className="text-xs text-red-500">(Campo obligatorio)</span>
                      </Label>
                      <Input
                        value={formData.bank}
                        onChange={(e) => { if (e.target.value.length <= MAX_BANK) updateField('bank', e.target.value); }}
                        maxLength={MAX_BANK}
                        placeholder="Ej: Banco Nación, Banco Galicia..."
                        className={`rounded-lg bg-background border-border focus-visible:ring-primary ${bankInvalid ? 'border-red-500 border-2 focus-visible:ring-red-500' : ''}`}
                      />
                      <AtLimit value={formData.bank} max={MAX_BANK} />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-foreground sm:text-sm">
                        Número de Cuenta <span className="text-xs text-red-500">(Campo obligatorio)</span>
                      </Label>
                      <Input
                        value={formData.accountNumber}
                        onChange={(e) => { if (e.target.value.length <= MAX_ACCOUNT) updateField('accountNumber', e.target.value); }}
                        maxLength={MAX_ACCOUNT}
                        placeholder="Número de cuenta"
                        className={`rounded-lg bg-background border-border focus-visible:ring-primary ${accountNumberInvalid ? 'border-red-500 border-2 focus-visible:ring-red-500' : ''}`}
                      />
                      <AtLimit value={formData.accountNumber} max={MAX_ACCOUNT} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-foreground sm:text-sm">CBU <span className="text-xs font-normal text-muted-foreground">(opcional)</span></Label>
                        <Input
                          value={formData.cbu}
                          onChange={(e) => {
                            const v = e.target.value.replace(/\D/g, '');
                            if (v.length <= MAX_CBU) {
                              updateField('cbu', v);
                              setValidationErrors((p) => ({ ...p, cbu: '' }));
                            }
                          }}
                          placeholder="22 dígitos"
                          className={`rounded-lg bg-background border-border focus-visible:ring-primary ${validationErrors.cbu ? 'border-red-500 border-2' : ''}`}
                        />
                        {validationErrors.cbu
                          ? <p className="text-xs text-destructive">{validationErrors.cbu}</p>
                          : <p className="text-xs text-muted-foreground">{formData.cbu.length}/{MAX_CBU}</p>
                        }
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs font-medium text-foreground sm:text-sm">Alias <span className="text-xs font-normal text-muted-foreground">(opcional)</span></Label>
                        <Input
                          value={formData.alias}
                          onChange={(e) => { if (e.target.value.length <= MAX_ALIAS) updateField('alias', e.target.value); }}
                          maxLength={MAX_ALIAS}
                          placeholder="Alias de la cuenta"
                          className="rounded-lg bg-background border-border focus-visible:ring-primary"
                        />
                        <AtLimit value={formData.alias} max={MAX_ALIAS} />
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* Sección: Saldo y Estado */}
            <div className="border-t border-border" />
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <div className="lg:col-span-1">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
                  <DollarSign className="w-4 h-4 text-primary" />
                  Saldo y Estado
                </h3>
                <p className="mt-1 text-xs text-muted-foreground">Balance inicial y estado de la caja.</p>
              </div>
              <div className="space-y-4 lg:col-span-2">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-foreground sm:text-sm">
                      {editingId ? 'Saldo Actual' : 'Saldo Inicial'}
                    </Label>
                    {editingId ? (
                      <div className="flex items-center h-10 px-3 text-sm font-semibold border rounded-lg border-border bg-muted/40 tabular-nums text-foreground">
                        {formatCurrency(formData.balance, formData.currency)}
                      </div>
                    ) : (
                      <MoneyInput
                        value={formData.balance === 0 ? '' : formData.balance}
                        onValueChange={(value) => updateField('balance', value ?? 0)}
                        currency={formData.currency}
                        placeholder="0,00"
                      />
                    )}
                    {editingId && (
                      <p className="text-xs text-muted-foreground">Solo cambia mediante ingresos, egresos o transferencias.</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-foreground sm:text-sm">Estado</Label>
                    <Select value={formData.isActive ? 'true' : 'false'} onValueChange={(v) => updateField('isActive', v === 'true')}>
                      <SelectTrigger className="rounded-lg bg-background border-border">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="true">Activa</SelectItem>
                        <SelectItem value="false">Inactiva</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="shrink-0 flex flex-col-reverse gap-3 px-6 py-4 border-t border-border sm:flex-row sm:justify-end">
            <Button variant="outline" onClick={closeCashBoxModal} disabled={saving} className="w-full rounded-lg sm:w-auto">
              Cancelar
            </Button>
            <RequirePermission permission={PERMISSIONS.CONTABILIDAD.CAJAS_OPERAR}>
              <Button
                onClick={handleSaveCashBox}
                disabled={saving}
                className="w-full rounded-lg bg-primary text-primary-foreground hover:opacity-90 sm:w-auto"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Guardando...' : editingId ? 'Guardar Cambios' : 'Crear Caja'}
              </Button>
            </RequirePermission>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modales de operaciones */}
      <IncomeModal
        isOpen={incomeModalOpen}
        onClose={() => { setIncomeModalOpen(false); setPreselectedCashBoxId(undefined); }}
        onSuccess={handleIncomeSuccess}
        preselectedCashBoxId={preselectedCashBoxId}
      />
      <ExpenseModal
        isOpen={expenseModalOpen}
        onClose={() => { setExpenseModalOpen(false); setPreselectedCashBoxId(undefined); }}
        onSuccess={handleExpenseSuccess}
        preselectedCashBoxId={preselectedCashBoxId}
      />
      <TransferModal
        isOpen={transferModalOpen}
        onClose={() => setTransferModalOpen(false)}
        onSuccess={handleTransferSuccess}
      />
      <CurrencyConversionModal
        isOpen={conversionModalOpen}
        onClose={() => setConversionModalOpen(false)}
        onSuccess={handleConversionSuccess}
      />

      {/* Modales de reportes */}
      <CashBoxSummaryModal
        isOpen={summaryModalOpen}
        onClose={() => { setSummaryModalOpen(false); setTimeout(() => setSelectedReportCashBoxId(null), 300); }}
        cashBoxId={selectedReportCashBoxId}
      />
      <CashBoxMovementsModal
        isOpen={movementsModalOpen}
        onClose={() => { setMovementsModalOpen(false); setTimeout(() => setSelectedReportCashBoxId(null), 300); }}
        cashBoxId={selectedReportCashBoxId}
      />
      <CashBoxExportModal
        isOpen={exportModalOpen}
        onClose={() => { setExportModalOpen(false); setTimeout(() => setSelectedReportCashBoxId(null), 300); }}
        cashBoxId={selectedReportCashBoxId}
      />

      {/* ==================== MODAL DESACTIVAR / ELIMINAR CAJA ==================== */}
      {deleteTargetBox && (() => {
        const box = deleteTargetBox;
        const hasBalance = (box.balance ?? 0) !== 0;
        const isActive = box.isActive ?? true;

        return (
          <Dialog open={deleteModalOpen} onOpenChange={(open) => { if (!open && !deleteAction) setDeleteModalOpen(false); }}>
            <DialogContent className="w-[95vw] max-w-md bg-card border-border p-0 flex flex-col">

              <DialogHeader className="shrink-0 px-6 pt-6 pb-4 border-b border-border">
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                    hasBalance ? 'bg-warning/10' : 'bg-destructive/10'
                  }`}>
                    <ShieldAlert className={`w-4 h-4 ${hasBalance ? 'text-warning' : 'text-destructive'}`} />
                  </div>
                  <div>
                    <DialogTitle className="text-base font-bold text-foreground">
                      {isActive ? 'Desactivar caja' : 'Eliminar caja'}
                    </DialogTitle>
                    <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-[240px]">{box.name}</p>
                  </div>
                </div>
              </DialogHeader>

              <div className="px-6 py-5 space-y-4">
                {/* Info de saldo */}
                <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-sm ${
                  hasBalance
                    ? 'bg-warning/5 border-warning/20 text-warning'
                    : 'bg-muted/40 border-border text-muted-foreground'
                }`}>
                  <DollarSign className="w-4 h-4 shrink-0" />
                  <span>
                    Saldo actual:{' '}
                    <span className="font-semibold tabular-nums">
                      {formatCurrency(box.balance ?? 0, box.currency)}
                    </span>
                  </span>
                </div>

                {/* Mensaje según caso */}
                {isActive && hasBalance && (
                  <div className="space-y-2">
                    <p className="text-sm text-foreground font-medium">Esta caja tiene saldo registrado.</p>
                    <p className="text-sm text-muted-foreground">
                      No es posible eliminarla mientras tenga movimientos o saldo. Podés <strong>desactivarla</strong>: quedará oculta de las operaciones pero conservará su historial.
                    </p>
                  </div>
                )}

                {isActive && !hasBalance && (
                  <div className="space-y-2">
                    <p className="text-sm text-foreground font-medium">La caja tiene saldo en cero.</p>
                    <p className="text-sm text-muted-foreground">
                      Podés <strong>desactivarla</strong> para dejar de usarla pero conservar el historial, o <strong>eliminarla</strong> de forma permanente si fue creada por error.
                    </p>
                    <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-destructive/5 border border-destructive/20 text-xs text-destructive">
                      <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                      La eliminación es irreversible y borrará todos los registros de esta caja.
                    </div>
                  </div>
                )}

                {!isActive && (
                  <div className="space-y-2">
                    <p className="text-sm text-foreground font-medium">Esta caja ya está desactivada.</p>
                    {hasBalance ? (
                      <p className="text-sm text-muted-foreground">
                        Tiene saldo registrado, por lo que no puede eliminarse. Si necesitás limpiar el saldo, reactivala y realizá un egreso primero.
                      </p>
                    ) : (
                      <>
                        <p className="text-sm text-muted-foreground">
                          El saldo es cero. Podés eliminarla de forma permanente si ya no la necesitás.
                        </p>
                        <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-destructive/5 border border-destructive/20 text-xs text-destructive">
                          <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                          La eliminación es irreversible y borrará todos los registros de esta caja.
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Footer con acciones según caso */}
              <div className="shrink-0 flex flex-col-reverse gap-2 px-6 py-4 border-t border-border sm:flex-row sm:justify-end">
                <Button
                  variant="outline"
                  onClick={() => setDeleteModalOpen(false)}
                  disabled={deleteAction}
                  className="w-full rounded-lg sm:w-auto"
                >
                  Cancelar
                </Button>

                {/* Desactivar: visible si la caja está activa */}
                {isActive && (
                  <Button
                    onClick={handleDeactivateCashBox}
                    disabled={deleteAction}
                    className="w-full rounded-lg bg-warning text-warning-foreground hover:opacity-90 sm:w-auto"
                  >
                    <PowerOff className="w-4 h-4 mr-2" />
                    {deleteAction ? 'Desactivando...' : 'Desactivar Caja'}
                  </Button>
                )}

                {/* Reactivar: visible si la caja está inactiva */}
                {!isActive && (
                  <Button
                    onClick={async () => { await handleReactivateCashBox(box.id); setDeleteModalOpen(false); }}
                    disabled={deleteAction}
                    className="w-full rounded-lg bg-success text-success-foreground hover:opacity-90 sm:w-auto"
                  >
                    <Power className="w-4 h-4 mr-2" />
                    Reactivar Caja
                  </Button>
                )}

                {/* Eliminar: solo si saldo = 0 (activa o inactiva) */}
                {!hasBalance && (
                  <Button
                    onClick={handleDeleteCashBox}
                    disabled={deleteAction}
                    variant="destructive"
                    className="w-full rounded-lg sm:w-auto"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    {deleteAction ? 'Eliminando...' : 'Eliminar Permanentemente'}
                  </Button>
                )}
              </div>
            </DialogContent>
          </Dialog>
        );
      })()}
      </div>
    </div>
  );
}
